import { useCallback, useEffect, useState } from 'react'
import ApiClient from 'api-client/ApiClient'
import { VMHAConditionKey } from 'api-client/model'
import { vmhaClusterHostRequirementsDoc, vmhaDoc } from 'app/docLinks'
import Alert from 'core/components/Alert'
import ExternalLink from 'core/components/ExternalLink'
import LearnMoreLink from 'core/components/LearnMoreLink'
import SelectableCard from 'core/components/SelectableCard'
import PicklistField from 'core/components/validatedForm/DropdownField'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { checkVMHAStatusForCluster } from '../actions'
import { updateClusterHostGroup } from './actions'
import CapacityReserveCard, {
  DEFAULT_PERCENT as DEFAULT_CAPACITY_RESERVE_PERCENT,
  MAX_PERCENT as MAX_CAPACITY_RESERVE_PERCENT,
  MIN_PERCENT as MIN_CAPACITY_RESERVE_PERCENT,
} from './CapacityReserveCard'
import ClusterAccessField from './ClusterAccessField'
import CpuModePicklist from './CpuModePicklist'
import { useStyles } from './CreateClusterHostGroupsModal'
import EvacuationSpeedSlider from './EvacuationSpeedSlider'
import { mapCpuModeToBody, mapVMHAConditions } from './helpers'
import {
  type EditClusterHostGroup,
  EvacuationSpeed,
  type IClusterHostGroup,
  RebalancingFrequency,
  RebalancingStrategy,
  type VmhaConfigBody,
} from './model'
import RebalancingFrequencyPickList from './RebalancingFrequencyPickList'
import VMHAConfirmationDialog from './VMHAConfirmationDialog'
import VmhaConditionsStatus from './VmhaConditionsStatus'

const { haMgr } = ApiClient.getInstance()

const EMPTY_TENANT_IDS: string[] = []

const gpuModes = [
  { label: 'Passthrough', value: 'passthrough' },
  { label: 'vGPU', value: 'vgpu' },
]

interface Props {
  rows: IClusterHostGroup[]
  onClose: (reload?: boolean) => void
}

export default function EditClusterHostGroupModal({ rows, onClose }: Props) {
  const classes = useStyles()
  const [gpuError, setGPUError] = useState<string | null>(null)
  const [vmhaClusterDetails, setVmhaClusterDetails] = useState()
  const [vmhaToggleDisabled, setVMHAToggleDisabled] = useState(true)
  const [isImageLibraryDegraded, setIsImageLibraryDegraded] = useState(false)
  const [vmhaClusterStatusLoading, setVmhaClusterStatusLoading] = useState(false)
  const [showConfirmVMHARiskModal, setShowConfirmVMHARiskModal] = useState(false)
  const [vmhaConfigError, setVmhaConfigError] = useState<string | null>(null)
  const [submitAttempted, setSubmitAttempted] = useState(false)

  // ECHGM-FR-017: [] (or absent) means Shared; GET /clusters already loaded this on `rows`
  const originalTenantIds = rows?.[0]?.tenants ?? EMPTY_TENANT_IDS
  const [capacityReserveError, setCapacityReserveError] = useState<string | null>(null)

  const defaultParams = {
    name: rows?.[0]?.name,
    vmHighAvailability: rows?.[0]?.vmHighAvailability?.enabled,
    autoResourceRebalancing: rows?.[0]?.autoResourceRebalancing?.enabled,
    rebalancingFrequency:
      rows?.[0]?.autoResourceRebalancing?.rebalancingFrequencyMins || RebalancingFrequency.Default,
    enableGpu: rows?.[0]?.gpu?.enabled,
    gpuType: rows?.[0]?.gpu?.mode,
    // ECHGM-FR-011: pre-populate CPU mode/model from the existing cluster
    cpuMode: rows?.[0]?.cpu?.mode || 'default',
    cpuModel: rows?.[0]?.cpu?.model || '',
    evacuationSpeed: EvacuationSpeed.Conservative,
    nfsHeartbeatPath: '',
    storageHeartbeatEnabled: false,
    excludeEphemeralVms: false,
    // ECHGM-FR-017: pre-populate Shared/tenantIds from the cluster's own `tenants` field
    shared: originalTenantIds.length === 0,
    tenantIds: originalTenantIds,
    capacityReserveEnabled: false,
    capacityReservePercent: DEFAULT_CAPACITY_RESERVE_PERCENT,
  }
  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)
  const {
    update,
    updating: updatingClusterHostGroup,
    error,
    reset,
  } = useUpdateAction(updateClusterHostGroup)

  const fetchVmhaClusterStatus = useCallback(async () => {
    if (!rows?.[0]?.name) return

    try {
      setVmhaClusterStatusLoading(true)

      const configs: any = await checkVMHAStatusForCluster(rows?.[0]?.name)

      // HA Slave status check is skipped for Enabling VMHA On cluster
      const conditions = mapVMHAConditions(configs, false)

      // Initialize flags
      let hasFailure = false
      let isImageLibraryDegraded = false

      // Check if any condition is failed or degraded
      // For ImageLibrary, we consider both passed and degraded as success
      // For other conditions, we only consider passed as success
      conditions?.forEach((c) => {
        if (c.key === VMHAConditionKey.ImageLibrary) {
          if (!(c.passed || c.degraded)) {
            hasFailure = true
          }
          isImageLibraryDegraded = c.degraded
        } else if (!c.passed) {
          hasFailure = true
        }
      })

      setIsImageLibraryDegraded(isImageLibraryDegraded)
      setVMHAToggleDisabled(hasFailure)
      setVmhaClusterDetails(configs)
    } catch (error) {
      console.error(error)
    } finally {
      setVmhaClusterStatusLoading(false)
    }
  }, [rows?.[0]?.name])

  useEffect(() => {
    fetchVmhaClusterStatus()
  }, [fetchVmhaClusterStatus])

  // ECHGM-FR-001/002/003/004: fetch VMHA config on open to pre-populate fields; errors are silent
  useEffect(() => {
    const clusterName = rows?.[0]?.name
    if (!clusterName || !rows?.[0]?.vmHighAvailability?.enabled) return
    haMgr
      .getVmhaConfig(clusterName)
      .then((config) => {
        updateParams({
          evacuationSpeed: config?.mode ?? EvacuationSpeed.Conservative,
          nfsHeartbeatPath: config?.host_config?.nfs_heartbeat_path ?? '',
          storageHeartbeatEnabled: !!config?.host_config?.nfs_heartbeat_path,
          // ECHGM-FR-014: absent key means the setting was never set — treat as false
          excludeEphemeralVms: !!config?.exclude_ephemeral_vms,
        })
      })
      .catch(() => {
        // ECHGM-FR-004: silent fallback — defaults already set
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // PCD-4129: fetch the configured capacity-reserve threshold on open to
  // pre-populate the toggle/percent fields; no threshold configured yet
  // (404) or any other failure falls back to the defaults silently.
  useEffect(() => {
    const clusterName = rows?.[0]?.name
    if (!clusterName || !rows?.[0]?.vmHighAvailability?.enabled) return
    haMgr
      .getAllocationThreshold(clusterName)
      .then((config) => {
        updateParams({
          capacityReserveEnabled: !!config?.capacity_reserve_enabled,
          capacityReservePercent:
            config?.allocation_threshold_percent ?? DEFAULT_CAPACITY_RESERVE_PERCENT,
        })
      })
      .catch(() => {
        // no threshold configured for this cluster yet — defaults already set
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // When enabling GPU, check if all hosts support the selected GPU mode
  useEffect(() => {
    if (
      params?.enableGpu &&
      params?.gpuType &&
      rows?.[0]?.hosts?.length > 0 &&
      rows?.[0]?.hosts?.some((host) => !host?.compatibleModes?.includes(params.gpuType))
    ) {
      setGPUError(`All hosts in the cluster must support the selected GPU mode.`)
    } else {
      setGPUError(null)
    }
  }, [params.gpuType, params.enableGpu])

  // PCD-4129: require a valid MIN_CAPACITY_RESERVE_PERCENT..MAX_CAPACITY_RESERVE_PERCENT
  // percent whenever the capacity reserve toggle is on, so submit can be
  // blocked the same way gpuError blocks it.
  useEffect(() => {
    if (!params.capacityReserveEnabled) {
      setCapacityReserveError(null)
      return
    }
    const value = Number(params.capacityReservePercent)
    if (
      Number.isNaN(value) ||
      value < MIN_CAPACITY_RESERVE_PERCENT ||
      value > MAX_CAPACITY_RESERVE_PERCENT
    ) {
      setCapacityReserveError(
        `Enter a reserved capacity percentage between ${MIN_CAPACITY_RESERVE_PERCENT} and ${MAX_CAPACITY_RESERVE_PERCENT}.`,
      )
    } else {
      setCapacityReserveError(null)
    }
  }, [params.capacityReserveEnabled, params.capacityReservePercent])

  const handleClose = useCallback(() => {
    setParams(defaultParams)
    setSubmitAttempted(false)
    reset()
    onClose()
  }, [setParams, reset, onClose])

  // ECHGM-FR-019/020: only send `tenants` when the target access list differs from the loaded
  // baseline — PUT replaces the whole list, so a no-op send on every edit has no benefit.
  const tenantAccessChanged = useCallback(() => {
    const targetTenantIds = params.shared ? [] : params.tenantIds
    if (targetTenantIds.length !== originalTenantIds.length) return true
    const originalSet = new Set(originalTenantIds)
    return targetTenantIds.some((id) => !originalSet.has(id))
  }, [originalTenantIds, params.shared, params.tenantIds])

  const submitForm = useCallback(async () => {
    setVmhaConfigError(null)
    setSubmitAttempted(true)
    // ECHGM-FR-022: block submit when dedicating to tenants but none are selected — otherwise
    // an empty tenantIds with the Shared toggle still OFF would send `tenants: []`, which per
    // the backend contract means Shared, silently contradicting the still-OFF toggle.
    if (!params.shared && params.tenantIds.length === 0) {
      return
    }
    const body: EditClusterHostGroup = {
      name: params.name,
      vmHighAvailability: { enabled: params.vmHighAvailability },
      autoResourceRebalancing: {
        enabled: params.autoResourceRebalancing,
        ...(params.autoResourceRebalancing && {
          rebalancingStrategy: RebalancingStrategy.VMWorkloadConsolidation,
          rebalancingFrequencyMins: params.rebalancingFrequency,
        }),
      },
      gpu: {
        enabled: params.enableGpu,
        mode: params.gpuType,
      },
      // ECHGM-FR-012: include updated CPU mode/model in the cluster update body
      cpu: mapCpuModeToBody(params.cpuMode, params.cpuModel),
      // ECHGM-FR-019/020: omit to keep, [] for Shared, a list to replace — only when changed
      ...(tenantAccessChanged() && { tenants: params.shared ? [] : params.tenantIds }),
    }
    const { success } = await update({ body })
    if (success) {
      // PCD-4129: pushed unconditionally (enabling AND disabling) right after
      // the resmgr call that actually changes vmHighAvailability.enabled, so
      // ClusterAllocationThresholdFilter's Nova-side view updates immediately
      // instead of waiting for hamgr's periodic sync tick. Best-effort: a
      // failure here doesn't block the cluster update or the sub-config saves
      // below, since the sync tick will still catch up to the resmgr change.
      try {
        await haMgr.setVMHAEnabled(params.name, params.vmHighAvailability)
      } catch {
        // Non-fatal -- see comment above.
      }

      // ECHGM-FR-007/008/009: post VMHA config only when VMHA is enabled
      if (params.vmHighAvailability) {
        // ECHGM-FR-008: always send host_config so clearing the NFS path removes it on the server
        const vmhaConfigBody: VmhaConfigBody = {
          mode: params.evacuationSpeed,
          exclude_ephemeral_vms: params.excludeEphemeralVms,
          host_config: {
            nfs_heartbeat_path: params.storageHeartbeatEnabled
              ? params.nfsHeartbeatPath.trim()
              : '',
          },
        }
        try {
          await haMgr.updateVmhaConfig(params.name, vmhaConfigBody)
        } catch {
          // ECHGM-FR-010: surface HaMgr error, keep modal open
          setVmhaConfigError(
            'Cluster was updated but VMHA configuration could not be saved. Please edit the cluster to retry.',
          )
          return
        }

        // PCD-4129: pushed unconditionally alongside the VM-HA config above --
        // both fields are required by the endpoint, and capacity_reserve_enabled
        // being false is what keeps the filter a no-op for clusters that never
        // opted into the reserve.
        try {
          await haMgr.updateAllocationThreshold(params.name, {
            allocation_threshold_percent: params.capacityReservePercent,
            capacity_reserve_enabled: params.capacityReserveEnabled,
          })
        } catch {
          setVmhaConfigError(
            'Cluster was updated but the failover capacity reserve could not be saved. Please edit the cluster to retry.',
          )
          return
        }
      }
      setParams(defaultParams)
      reset()
      onClose(true)
    }
  }, [params, update, onClose, setParams, reset, defaultParams, tenantAccessChanged])

  const handleVMHAToggle = useCallback(
    (value: boolean) => {
      // If VMHA is being enabled and the image library is degraded, show the risk confirmation modal
      if (!params.vmHighAvailability && isImageLibraryDegraded) {
        setShowConfirmVMHARiskModal(true)
      } else {
        getParamsUpdater('vmHighAvailability')(value)
      }
    },
    [params.vmHighAvailability, isImageLibraryDegraded, getParamsUpdater],
  )
  // Reset VM HA confirmation modal state
  const resetVMHAConfirmationModal = useCallback(() => {
    setShowConfirmVMHARiskModal(false)
  }, [setShowConfirmVMHARiskModal])

  const enableVMHAToggle = useCallback(() => {
    getParamsUpdater('vmHighAvailability')(true)
    resetVMHAConfirmationModal()
  }, [getParamsUpdater, resetVMHAConfirmationModal])

  return (
    <ModalForm
      open={true}
      title={`Edit Cluster`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updatingClusterHostGroup}
      error={error}
      initialValues={defaultParams}
      submitTitle={`Update Cluster`}
      disableSubmit={!!gpuError || !!capacityReserveError}
      panel={'drawer'}
    >
      {vmhaConfigError && (
        <>
          <Alert variant="error" title="VMHA Configuration Failed">
            {vmhaConfigError}
          </Alert>
          <br />
        </>
      )}
      <FormFieldSection title="Cluster Configuration" className={classes.customFormFieldSection}>
        <TextField
          id="name"
          label="Name"
          onChange={getParamsUpdater('name')}
          value={params.name}
          readOnly
        />
        {/* ECHGM-FR-013: CPU Mode/Model edit fields */}
        <PicklistField
          DropdownComponent={CpuModePicklist}
          id="cpuMode"
          onChange={getParamsUpdater('cpuMode')}
          value={params.cpuMode}
          label="CPU Mode"
          info={
            <div>
              <div>Controls how the hypervisor exposes CPU features to VMs.</div>
              <br />
              <div>Default: Select the latest supported CPU model on the host.</div>
              <br />
              <div>
                Custom: Specify a standardized CPU model to enable live migration between hosts with
                different physical CPUs.
              </div>
              <br />
              <div>
                Host Model: Closely matches the physical CPU for balanced performance and migration
                flexibility.
              </div>
              <br />
              <div>
                Host Passthrough: Directly exposes the physical CPU for maximum performance but
                limits migration to identical hardware.
              </div>
            </div>
          }
        />
        {params.cpuMode === 'custom' && (
          <TextField
            id="cpuModel"
            label="CPU Model"
            onChange={getParamsUpdater('cpuModel')}
            value={params.cpuModel}
            placeholder="eg. Nehalem"
            required
            info="The CPU model that the virtualization layer (hypervisor) exposes to virtual machines (VMs). Used to standardize CPU features across hosts on this cluster to enable live migration between hosts. This may differ from the physical host CPU model."
          />
        )}
        <div className={classes.toggleSwitch}>
          <ToggleSwitchField
            id="vmHighAvailability"
            label="VM High Availability"
            value={params.vmHighAvailability}
            onChange={handleVMHAToggle}
            customLabel={
              <div className={classes.customLabel}>
                <Text variant="caption1" className={classes.customLabelTitle}>
                  VM High Availability
                  <HintTooltip message="Automatically detect host failure and recover VMs on other host group hosts." />
                </Text>
              </div>
            }
            // Disable the toggle if VMHA is not enabled
            // And either VMHA is disabled or VMHA cluster status is loading
            disabled={
              !defaultParams.vmHighAvailability && (vmhaToggleDisabled || vmhaClusterStatusLoading)
            }
          />
          {/* If the image library is degraded, show the VMHA risk confirmation modal */}
          <VMHAConfirmationDialog
            open={showConfirmVMHARiskModal}
            onCancel={resetVMHAConfirmationModal}
            onConfirm={enableVMHAToggle}
            allConditionsMet={false} // Since we only show this modal when the image library is degraded, we can set this to false
          />
          <div className={classes.vmhaToggleSwitchWarning}>
            <VmhaConditionsStatus
              title={
                defaultParams.vmHighAvailability ? (
                  <>
                    The cluster will reach 'Protected' status only when all prerequisites below are
                    satisfied. Partial or unmet conditions will result in 'Degraded' or 'Not
                    Protected' states.{' '}
                    <ExternalLink
                      variant="caption2"
                      url={vmhaClusterHostRequirementsDoc}
                      icon="external-link"
                    >
                      <b>More Info</b>
                    </ExternalLink>
                  </>
                ) : (
                  'All conditions must be met to enable VMHA -'
                )
              }
              conditions={mapVMHAConditions(vmhaClusterDetails, false)}
              loading={vmhaClusterStatusLoading}
              plainText
            />
          </div>
        </div>
        {/* ECHGM-FR-015: exclude-ephemeral-VMs toggle, below the VMHA prereqs, only while VMHA is ON */}
        {params.vmHighAvailability && (
          <>
            <div className={classes.excludeEphemeralVmsToggle}>
              <ToggleSwitchField
                id="excludeEphemeralVms"
                label="Exclude all ephemeral VMs from evacuations"
                value={params.excludeEphemeralVms}
                onChange={(value) => getParamsUpdater('excludeEphemeralVms')(value)}
                customLabel={
                  <div className={classes.customLabel}>
                    <Text variant="caption1" className={classes.customLabelTitle}>
                      Exclude all ephemeral VMs from evacuations
                    </Text>
                    <Text variant="body2">
                      Enabling the toggle will exclude all ephemeral VMs (those with local storage
                      and shared storage).
                    </Text>
                  </div>
                }
              />
              {/* ECHGM-FR-016: link to VM HA docs, alongside the exclude-ephemeral-VMs toggle */}
              <LearnMoreLink url={vmhaDoc} text="Learn More about VM HA" />
            </div>
            {/* PCD-4129: capacity reserve, above the evacuation speed panel */}
            <CapacityReserveCard
              enabled={params.capacityReserveEnabled}
              onEnabledChange={(value) => getParamsUpdater('capacityReserveEnabled')(value)}
              percent={params.capacityReservePercent}
              onPercentChange={getParamsUpdater('capacityReservePercent')}
              className={classes.rebalancingFrequency}
            />
            {/* ECHGM-FR-005/006: show VMHA config fields when VMHA is ON */}
            <EvacuationSpeedSlider
              value={params.evacuationSpeed}
              onChange={(value) => getParamsUpdater('evacuationSpeed')(value)}
              className={classes.rebalancingFrequency}
              params={params}
              getParamsUpdater={getParamsUpdater}
            />
          </>
        )}

        <div className={classes.toggleSwitch}>
          <ToggleSwitchField
            id="autoResourceRebalancing"
            label="Dynamic Resource Rebalancing"
            value={params.autoResourceRebalancing}
            onChange={(value) => getParamsUpdater('autoResourceRebalancing')(value)}
            customLabel={
              <div className={classes.customLabel}>
                <Text variant="caption1" className={classes.customLabelTitle}>
                  Dynamic Resource Rebalancing
                  <HintTooltip message="Dynamically rebalances workloads across hosts in the host group to optimize resource utilization and prevent overload." />
                </Text>
                <Text variant="body2">
                  Dynamically rebalance workloads across hosts in the host group.
                </Text>
              </div>
            }
          />
        </div>
        {params.autoResourceRebalancing && (
          <RebalancingFrequencyPickList
            value={params.rebalancingFrequency}
            onChange={(value) => getParamsUpdater('rebalancingFrequency')(value)}
            className={classes.rebalancingFrequency}
          />
        )}
        <div className={classes.toggleSwitch}>
          <ToggleSwitchField
            id="enableGpu"
            label="Enable GPU"
            value={params.enableGpu}
            onChange={(value) => {
              updateParams({ enableGpu: value, gpuType: gpuModes[0].value })
            }}
            customLabel={
              <div className={classes.customLabel}>
                <Text variant="caption1" className={classes.customLabelTitle}>
                  Enable GPU
                  <HintTooltip message="Enable GPU support for this cluster to allow GPU passthrough, or vGPU-based virtual machines. This must be turned on before GPU configuration or assignment." />
                </Text>
                <Text variant="body2">Use this feature to enable GPU support for the cluster.</Text>
              </div>
            }
          />
        </div>
        {params.enableGpu && (
          <div className={classes.gpuFields}>
            <Text variant="subtitle2">GPU Mode</Text>
            <Text variant="body2">
              Choose the type of GPU support you want this flavor to provide. Use <b>Passthrough</b>{' '}
              to assign full physical GPUs, or <b>vGPU</b> to use virtual GPU slices.
            </Text>
            <div className={classes.gpuCards}>
              {gpuModes.map(({ label, value }) => (
                <SelectableCard
                  id={value}
                  onClick={() => updateParams({ gpuType: value })}
                  active={params.gpuType === value}
                  withCustomBody
                  showRadioButton
                  className={classes.card}
                  // disabled={disabled}
                  // showCheckmarkIcon
                >
                  <Text variant="caption1">{label}</Text>
                </SelectableCard>
              ))}
            </div>
            {gpuError && <ErrorMessage>{gpuError}</ErrorMessage>}
          </div>
        )}
        <div className={classes.toggleSwitch}>
          <ToggleSwitchField
            id="shared"
            label="Shared With All Tenants"
            value={params.shared}
            onChange={getParamsUpdater('shared')}
            customLabel={
              <div className={classes.customLabel}>
                <Text variant="caption1" className={classes.customLabelTitle}>
                  Shared With All Tenants
                  <HintTooltip message="When off, only the selected tenants can see and schedule VMs on this cluster. Shared clusters remain usable by every tenant." />
                </Text>
                <Text variant="body2">
                  Choose whether this cluster is shared with every tenant across all domains, or
                  dedicated to specific tenants only.
                </Text>
              </div>
            }
          />
        </div>
        {/* ECHGM-FR-018: tenant access field, pre-populated from the cluster's current list */}
        {!params.shared && (
          <>
            {submitAttempted && params.tenantIds.length === 0 && (
              <div className={classes.tenantAccessField}>
                <ErrorMessage>Select at least one tenant, or turn on Shared.</ErrorMessage>
              </div>
            )}
            <ClusterAccessField
              className={classes.tenantAccessField}
              value={params.tenantIds}
              onChange={(ids) => updateParams({ tenantIds: ids })}
            />
          </>
        )}
      </FormFieldSection>
    </ModalForm>
  )
}
